/**
 * Row distance + place meta for shops directory.
 * Gated only on live user coordinates — never on `sort === 'distanceAsc'`.
 */
import { shopDistanceMeters } from '../../shared/ui/directoryDistance.js';
import { geoPlaceCellKey } from '../../shared/ui/directoryGeoPlace.js';

export type ShopDirectoryDistanceMetaItem = {
  readonly lat?: number | null;
  readonly lng?: number | null;
  /** ONLINE_ONLY → no distance/place meta (A11c). */
  readonly customerPresence?: 'STOREFRONT' | 'ONLINE_ONLY' | string | null;
};

export type ShopDirectoryDistanceMetaInput = {
  readonly item: ShopDirectoryDistanceMetaItem;
  readonly userLat?: number | null;
  readonly userLng?: number | null;
  /** Cell-keyed map from {@link useDirectoryGeoPlaceLabels}; optional. */
  readonly placeLabels?: ReadonlyMap<string, string> | null;
};

export type ShopDirectoryDistanceMeta = {
  readonly distanceMeters: number | null;
  readonly placeLabel: string | null;
};

/**
 * Compute distance meters + place label when the user has a live fix.
 * Sort id is intentionally not an input — leaving `distanceAsc` must not clear meta.
 */
export function buildShopDirectoryDistanceMeta(
  input: ShopDirectoryDistanceMetaInput,
): ShopDirectoryDistanceMeta {
  const { item, userLat, userLng, placeLabels } = input;
  if (
    typeof userLat !== 'number' ||
    typeof userLng !== 'number' ||
    !Number.isFinite(userLat) ||
    !Number.isFinite(userLng)
  ) {
    return { distanceMeters: null, placeLabel: null };
  }

  // ONLINE_ONLY is never proximity-eligible — no meters / place cell (A11c).
  if (item.customerPresence === 'ONLINE_ONLY') {
    return { distanceMeters: null, placeLabel: null };
  }

  const distanceMeters = shopDistanceMeters(item, userLat, userLng);

  const itemLat = item.lat;
  const itemLng = item.lng;
  let placeLabel: string | null = null;
  if (
    placeLabels != null &&
    typeof itemLat === 'number' &&
    typeof itemLng === 'number' &&
    Number.isFinite(itemLat) &&
    Number.isFinite(itemLng)
  ) {
    placeLabel = placeLabels.get(geoPlaceCellKey(itemLat, itemLng)) ?? null;
  }

  return { distanceMeters, placeLabel };
}
